import { isEqual } from 'underscore';
import {
    namespace, Cache, Logger, Line, Point, Distance,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import MoveItemsCommand from '../command/moveItemsCommand';

/**
 * @extends CollisionManager
 */
export default class InsideTabletopManager {
    /**
     * Initializes the inside tabletop manager
     *
     * @param {Ofcs.Editor.Command.CommandExecutor} commandExecutor - Global command executor
     */
    constructor(commandExecutor) {
        this.tabletop = null;
        this.cache = new Cache();
        this.newCommand = {};
        this.edge = {};

        commandExecutor.on('beforeExecute', (...args) => this._onExecute(...args));
    }

    /**
     * Bind container item to this collision manager
     *
     * @param {Ofcs.Editor.Item.TabletopItem} item
     */
    bindContainerItem(item) {
        this.tabletop = item;
    }

    /**
     * Wrapper function to be called upon execution
     * will trigger moveStart event every time a
     * new initialPosition is detected
     *
     * @param {MoveItemsCommand} command moveItemCommand
     * @private
     */
    _onExecute(command) {
        if (command instanceof MoveItemsCommand && command.isValid()) {
            Logger.debug('insideTabletopManager._onExecute() | MoveItemsCommand');
            if (!isEqual(this.newCommand.initialPositions, command.initialPositions)) {
                this.newCommand = command;
                this._onMoveStart();
                this.setItemCollection(command);
                this.center = this.getCenter();
            }
            if (command.items.length > 1 || (command.items.length === 1 && command.items[0].supportsMove)) {
                this._onMove(command);
            }
        }

        if (command instanceof Ofcs.Editor.Command.ResizeItemCommand && command.item.supportsInsideTable === 'edge') {
            this._onResize(command.item);
        }
    }

    /**
     * called only when a new movement starts
     * @private
     */
    _onMoveStart() {
        this.cache.clear();
        this.edge = {};

        if (this.itemCollection) this.itemCollection.off('change');

        delete this.itemCollection;
        delete this.toolVersion;
    }

    /**
     * called on each move event
     * @param {Ofcs.Editor.Command.MoveItemsCommand} command moveItemCommand
     * @private
     */
    _onMove(command) {
        this.matrix = new Ofcs.Matrix();
        this.matrix.translate(command.distance.point.x, command.distance.point.y);

        ['x', 'y'].forEach(val => this.check(command, val));

        if (command.items.length === 1 && command.items[0].supportsInsideTable === 'edge') {
            command.items[0].updateEdge(this.edge.x, this.edge.y);
        }
    }

    /**
     * Called on Resize Events
     * will check is snapped Edges are sill touching
     * tabletop outlines and update margins accordingly
     *
     * @param {Ofcs.Editor.Item.AbstractItem} item
     * @private
     */
    _onResize(item) {
        const edges = item.getSnappedEdges();
        let y;
        let x;

        if (edges.length) {
            edges.forEach((val) => {
                if (this.edgeIsSnapped(item, val)) {
                    if (val === 0 || val === 2) {
                        y = val;
                    }
                    if (val === 1 || val === 3) {
                        x = val;
                    }
                }
            });
        }

        item.updateEdge(x, y);
    }

    /**
     * Checks if edge is snapped for given item and position
     *
     * @param {Ofcs.Editor.Item.AbstractItem} item
     * @param {number} side - 0 = top, 1 = right, 2 = bottom, 3 = left
     * @return {boolean}
     */
    edgeIsSnapped(item, side) {
        const itemPoint = this.getItemEdgePoint(item, side);
        const tableLine = this.getTableLine(side);

        if (itemPoint && tableLine) {
            return itemPoint.getDistanceToLine(tableLine) === 0;
        }

        return false;
    }

    /**
     * Looks up point on item's given
     * edge side
     *
     * @param {Ofcs.Editor.Item.AbstractItem} item
     * @param {number} side - 0 = top, 1 = right, 2 = bottom, 3 = left
     * @return {Ofcs.Point}
     */
    getItemEdgePoint(item, side) {
        let point;

        switch (side) {
            case 0:
                point = item.getPointTop();
                break;
            case 1:
                point = item.getPointRight();
                break;
            case 2:
                point = item.getPointBottom();
                break;
            case 3:
                point = item.getPointLeft();
                break;
            default:
                point = false;
        }

        return point;
    }

    /**
     * Returns tableOutline for given side
     *
     * @param {number} side - 0 = top, 1 = right, 2 = bottom, 3 = left
     * @return {Ofcs.Line}
     */
    getTableLine(side) {
        const path = this.tabletop.originalPath;

        if (side > -1 && side < 4) {
            return new Line(path[side], path[side + 1]);
        }

        return null;
    }

    /**
     * initiates a new itemCollection for all items in the given command
     *
     * @param {Ofcs.Editor.Command.MoveItemsCommand} command moveItemCommand
     */
    setItemCollection(command) {
        this.itemCollection = new Ofcs.Editor.Item.ItemCollection();

        command.items.forEach(item => this.itemCollection.add(item));

        this.itemCollection.on('change', this._onItemCollectionChange, this);
    }

    /**
     * calculates a given bound's center position
     * @return {Ofcs.Point}
     */
    getCenter() {
        const bounds = this.getBounds();
        const top = bounds.getTopLeft().y;
        const bottom = bounds.getBottomRight().y;
        const left = bounds.getTopLeft().x;
        const right = bounds.getBottomRight().x;

        return new Point(
            left + ((right - left) / 2),
            top + ((bottom - top) / 2),
        );
    }

    /**
     * Is called when any item in the given collection shows a change
     * @param {Ofcs.Editor.Item.AbstractItem} item
     * @private
     */
    _onItemCollectionChange({ changed }) {
        if (changed.toolVersion !== undefined && changed.toolVersion !== this.toolVersion) {
            this.cache.clear();

            // TODO: get rid of this.Toolversion and find out why change is triggered multiple times
            this.toolVersion = changed.toolVersion;
        }
    }

    /**
     * checks limit conditions
     *
     * @param {Ofcs.Editor.Command.MoveItemsCommand} command moveItemCommand
     * @param {string} axis
     */
    check(command, axis) {
        if (!axis) return;

        const marginCorrection = this.getMarginCorrection(axis);
        const boundCorrection = this.getBoundCorrection(axis);

        if (marginCorrection.point[axis] !== 0) {
            let correction = marginCorrection;

            if (command.items.length === 1
                && command.items[0].supportsInsideTable === 'edge'
                && boundCorrection.point[axis] !== 0
            ) {
                correction = boundCorrection;
            }

            // eslint-disable-next-line no-param-reassign
            command.distance.point[axis] -= correction.point[axis];
        }
    }

    /**
     * Calculates aggregated item bounds
     *
     * @return {Bounds}
     */
    getBounds() {
        return this.cache.get('bounds', () => this.itemCollection.getBounds());
    }

    /**
     * Calculates aggregated item margin bounds
     *
     * @return {Bounds}
     */
    getMarginBounds() {
        return this.cache.get('marginBounds', () => this.itemCollection.getMarginBounds());
    }

    /**
     * Calculates correction parameters for margin bounds
     * @param {string} axis
     * @return {*}
     */
    getMarginCorrection(axis) {
        const saneAxis = axis.toUpperCase();
        const method = `calculate${saneAxis}Correction`;
        return this[method](
            this.getTableLimits(saneAxis),
            this.getLimits('getMarginBounds', saneAxis),
        );
    }

    /**
     * Calculates correction parameters for bounds
     * @param {string} axis
     * @return {*}
     */
    getBoundCorrection(axis) {
        const saneAxis = axis.toUpperCase();
        const method = `calculate${saneAxis}Correction`;
        return this[method](
            this.getTableLimits(saneAxis),
            this.getLimits('getBounds', saneAxis),
            true,
        );
    }

    /**
     * calculates correction distance on x-axis
     * when 'edge' === true, edge updates will be calculated as well
     *
     * @param {Object} table
     * @param {Object} item
     * @param {boolean} [edge]
     * @return {Distance}
     */
    calculateXCorrection(table, item, edge) {
        let temp = table.right - item.right;
        let x = temp <= 0 ? -temp : 0;
        const y = 0;

        this.edge.x = temp <= 0 && edge ? 1 : undefined;
        if (temp > 0) {
            temp = item.left - table.left;
            x = temp <= 0 ? temp : 0;
            this.edge.x = temp <= 0 && edge ? 3 : undefined;
        }

        return new Distance(x, y);
    }

    /**
     * calculates correction distance on y-axis
     * when 'edge' === true, edge updates will be calculated as well
     *
     * @param {Object} table
     * @param {Object} item
     * @param {boolean} [edge]
     * @return {Distance}
     */
    calculateYCorrection(table, item, edge) {
        let temp = item.top - table.top;
        let y = temp <= 0 ? temp : 0;
        const x = 0;

        this.edge.y = temp <= 0 && edge ? 0 : undefined;
        if (temp > 0) {
            temp = table.bottom - item.bottom;
            y = temp <= 0 ? -temp : 0;
            this.edge.y = temp <= 0 && edge ? 2 : undefined;
        }

        return new Distance(x, y);
    }

    /**
     * calculates current Limits based on actual movement distance
     * i.e. top & bottom for y-axis or left & right for x-axis
     *
     * @param {string} method
     * @param {string} axis
     * @return {Object}
     */
    getLimits(method, axis) {
        const distance = this.getBoundDistance(method, axis);
        const center = this.center.clone().transform(this.matrix);

        if (axis === 'X') {
            return {
                left: center.x - distance.left,
                right: center.x + distance.right,
            };
        }
        return {
            top: center.y - distance.top,
            bottom: center.y + distance.bottom,
        };
    }

    /**
     * stores relative distance to bounds' center
     * i.e. top & bottom for y-axis or left & right for x-axis
     *
     * @param {string} method
     * @param {string} axis
     * @return {Object}
     */
    getBoundDistance(method, axis) {
        return this.cache.get(`distance${method}${axis}`, () => {
            const bounds = this[method]();
            const center = this.getCenter();

            if (axis === 'X') {
                return {
                    left: center.x - bounds.getTopLeft().x,
                    right: bounds.getBottomRight().x - center.x,
                };
            }
            return {
                top: center.y - bounds.getTopLeft().y,
                bottom: bounds.getBottomRight().y - center.y,
            };
        });
    }

    /**
     * Returns table limits by axis
     * i.e. top & bottom for y-axis or left & right for x-axis
     *
     * @param {string} axis
     * @return {Object}
     */
    getTableLimits(axis) {
        return this.cache.get(`getTablePoints${axis}`, () => {
            if (axis === 'X') {
                return {
                    left: 0,
                    right: this.tabletop.get('width'),
                };
            }
            return {
                top: 0,
                bottom: this.tabletop.get('height'),
            };
        });
    }
}

namespace('Ofcs.Editor.Collision');
window.Ofcs.Editor.Collision.InsideTabletopManager = InsideTabletopManager;
